// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
// SVG metadata renderer — blocks and column headers.

import { Angles } from "../core/constants.js";
import {
  hasMetadata,
  hasMetadataHeaders,
  isOrthogonal,
  getPixelOffsets,
  getMetadataColumnWidth,
  getMetadataHeaderFontSize,
  getBlocksData,
  getHeadersData,
  makePixelOffsetAccessor,
} from "../core/metadata.js";
import { el, rgba } from "./dom.js";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render metadata colour blocks as an SVG <g> element containing one <rect>
 * per (leaf, column) pair that has a colour value.
 *
 * Returns null when metadata is disabled or no leaves have entries.
 *
 * Rect placement math:
 *   x = position[0] + (offsetX + pixelOffset) * cos(angle)
 *   y = position[1] + (offsetX + pixelOffset) * sin(angle)
 *   transform = rotate(degrees, x, y)
 *
 * @param {object} graph - Built graph returned by buildGraph().
 * @param {object} props - Merged rendering props.
 * @returns {SVGGElement|null}
 */
export function renderMetadataBlocks(graph, props) {
  if (!hasMetadata(props)) return null;

  const pixelOffset = getPixelOffsets(graph, props).length;
  const blockWidth = props.blockSize ?? 16;
  const blockHalfWidth = blockWidth / 2;
  const stepSize = graph.stepScale ?? blockWidth;
  const blockHeight =
    !isOrthogonal(props.type) || stepSize > blockWidth ? blockWidth : stepSize;
  const blockHalfHeight = blockHeight / 2;

  const rects = [];

  for (const datum of getBlocksData(graph, props)) {
    const degrees =
      (datum.node.angle / Angles.Degrees360) * 360 +
      (datum.node.inverted ? 180 : 0);

    const x =
      datum.position[0] +
      (datum.offsetX + pixelOffset) * Math.cos(datum.node.angle);
    const y =
      datum.position[1] +
      (datum.offsetX + pixelOffset) * Math.sin(datum.node.angle);

    rects.push(
      el("rect", {
        x: x - blockHalfWidth,
        y: y - blockHalfHeight,
        width: blockWidth,
        height: blockHeight,
        transform: `rotate(${degrees},${x},${y})`,
        fill: rgba(datum.colour),
        "data-node-id": datum.node.id,
        "data-col": datum.columnName,
      }),
    );
  }

  if (rects.length === 0) return null;

  return el("g", { class: "metadata-blocks" }, rects);
}

/**
 * Render metadata column header labels as an SVG <g> element containing one
 * <text> per column in props.blocks.
 *
 * Returns null when headers are disabled (see hasMetadataHeaders).
 *
 * Text placement math:
 *   [dx, dy] = pixelOffsetAccessor(datum)
 *   x = position[0] + dx
 *   y = position[1] + dy
 *   degrees = datum.angle + 180
 *
 * @param {object} graph - Built graph returned by buildGraph().
 * @param {object} props - Merged rendering props.
 * @returns {SVGGElement|null}
 */
export function renderMetadataHeaders(graph, props) {
  if (!hasMetadataHeaders(graph, props)) return null;

  const fontSize = getMetadataHeaderFontSize(props);
  const fontFamily = (props.fontFamily ?? "monospace").replace(/"/g, "'");
  const pixelOffsetAccessor = makePixelOffsetAccessor(graph, props);

  const texts = [];

  for (const datum of getHeadersData(graph, props)) {
    const [offsetX, offsetY] = pixelOffsetAccessor(datum);
    const x = datum.position[0] + offsetX;
    const y = datum.position[1] + offsetY;
    const degrees = datum.angle + 180;

    texts.push(
      el(
        "text",
        {
          x,
          y,
          "text-anchor": datum.inverted ? "end" : "start",
          "dominant-baseline": "middle",
          transform: `rotate(${degrees},${x},${y})`,
        },
        [String(datum.text)],
      ),
    );
  }

  if (texts.length === 0) return null;

  return el(
    "g",
    {
      class: "metadata-headers",
      "font-family": fontFamily,
      "font-size": fontSize,
    },
    texts,
  );
}
